import { useState } from 'react';
import { Link, useLocation, useParams, useSearchParams } from 'react-router';
import { AttachmentsCard } from '../../components/AttachmentsCard';
import { Gantt, type GanttRow } from '../../components/Gantt';
import { Alert, Badge, Button, Card, Spinner } from '../../components/ui';
import { type MessageKey, t } from '../../i18n';
import { errorMessage } from '../../lib/errors';
import { formatDay, planOrder, type ProjectDetail, projectStatusName, projectStatusTone, todayIso, useProject, useProjectForm } from './data';
import { ProjectCanchaCard } from './CanchaPage';
import { KanbanBoard } from './KanbanBoard';
import { ProjectDocuments } from './ProjectDocuments';
import { ProjectEditCard } from './ProjectEditCard';
import { ProjectPlan } from './ProjectPlan';
import { Progress } from './ProjectsPage';
import { priorityName } from './requests-data';

const VIEWS = ['lista', 'tablero', 'cronograma'] as const;
type View = (typeof VIEWS)[number];

/** Detalle de un proyecto (guía 7.2): el plan en lista, tablero Kanban o línea de tiempo; datos, documentos y adjuntos. */
export function ProjectDetailPage() {
  const { number = '' } = useParams();
  const location = useLocation();
  const [params, setParams] = useSearchParams();
  const project = useProject(number);
  const form = useProjectForm();
  const [editing, setEditing] = useState(false);
  const view: View = (VIEWS as readonly string[]).includes(params.get('vista') ?? '') ? (params.get('vista') as View) : 'lista';

  if (!project.data) return project.error ? <Alert>{errorMessage(project.error)}</Alert> : <Spinner label={t('comun.cargando')} />;
  const p = project.data;
  const created = (location.state as { created?: boolean } | null)?.created;

  return (
    <div className="space-y-4">
      <div>
        <Link to="/proyectos" className="text-sm text-marca hover:underline">
          ← {t('pr.titulo')}
        </Link>
        <h1 className="mt-1 text-2xl font-semibold text-tinta">
          <span className="mr-2 font-mono text-base text-slate-500">{p.code}</span>
          {p.name}
        </h1>
        <div className="mt-1 flex flex-wrap items-center gap-2 text-sm">
          <Badge tone={projectStatusTone(p.status)}>{projectStatusName(p.status)}</Badge>
          {p.priority && <span className="text-slate-600">· {t('pr.prioridad_corta', { prioridad: priorityName(p.priority) })}</span>}
          {p.labels.map((l) => (
            <span key={l.id} className="rounded px-1.5 py-0.5 text-xs font-medium text-white" style={{ backgroundColor: l.color }}>
              {l.name}
            </span>
          ))}
        </div>
      </div>
      {created && <Alert tone="success">{t('pr.creado', { codigo: p.code })}</Alert>}

      <div className="grid gap-4 xl:grid-cols-[1fr_22rem]">
        <div className="min-w-0 space-y-3">
          <div role="tablist" aria-label={t('pr.vistas')} className="flex gap-1 border-b border-slate-200">
            {VIEWS.map((v) => (
              <button
                key={v}
                type="button"
                role="tab"
                aria-selected={v === view}
                className={`-mb-px border-b-2 px-3 py-2 text-sm ${v === view ? 'border-marca font-medium text-slate-900' : 'border-transparent text-slate-600 hover:text-slate-900'}`}
                onClick={() => setParams(v === 'lista' ? {} : { vista: v }, { replace: true })}
              >
                {t(`pr.vista_${v}` as MessageKey)}
              </button>
            ))}
          </div>
          {view === 'lista' && <ProjectPlan project={p} />}
          {view === 'tablero' && (form.data ? <KanbanBoard project={p} statuses={form.data.taskStatuses} /> : <Spinner label={t('comun.cargando')} />)}
          {view === 'cronograma' && <Gantt rows={ganttRows(p)} today={todayIso()} label={t('pr.vista_cronograma')} />}
        </div>

        <div className="space-y-4">
          {editing ? (
            <ProjectEditCard project={p} onClose={() => setEditing(false)} />
          ) : (
            <Card variant="aside" title={t('pr.datos')}>
              <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1.5 text-sm">
                <dt className="text-slate-500">{t('pr.organizacion')}</dt>
                <dd>{p.organization ?? '—'}</dd>
                <dt className="text-slate-500">{t('pr.tipo')}</dt>
                <dd>{p.projectType ?? '—'}</dd>
                <dt className="text-slate-500">{t('pr.responsable')}</dt>
                <dd>{p.owner ?? <span className="text-slate-400">{t('pr.sin_responsable')}</span>}</dd>
                <dt className="text-slate-500">{t('pr.inicio')}</dt>
                <dd>{p.startDate ? formatDay(p.startDate) : '—'}</dd>
                <dt className="text-slate-500">{t('pr.vence')}</dt>
                <dd>{p.dueDate ? formatDay(p.dueDate) : '—'}</dd>
                {p.request && (
                  <>
                    <dt className="text-slate-500">{t('pr.solicitud')}</dt>
                    <dd>
                      <Link to={`/proyectos/solicitudes/${p.request.number}`} className="text-marca hover:underline">
                        {p.request.code}
                      </Link>
                    </dd>
                  </>
                )}
                <dt className="text-slate-500">{t('pr.avance')}</dt>
                <dd>
                  <Progress done={p.progress.done} total={p.progress.total} />
                </dd>
              </dl>
              {p.description && <p className="mt-3 whitespace-pre-wrap border-t border-slate-100 pt-3 text-sm text-slate-700">{p.description}</p>}
              {p.can.manage && (
                <Button variant="secondary" className="mt-3" onClick={() => setEditing(true)}>
                  {t('pr.editar')}
                </Button>
              )}
            </Card>
          )}
          <ProjectCanchaCard number={p.number} />
          <Link to={`/tareas/resumen?proyecto=${p.number}`} className="block text-sm text-marca hover:underline">
            {t('pr.ver_resumen')}
          </Link>
          <ProjectDocuments project={p} />
          <AttachmentsCard entity="project" entityId={p.id} canUpload={p.can.contribute} canManage={p.can.manage} help={t('pr.adjuntos_ayuda')} />
        </div>
      </div>
    </div>
  );
}

/**
 * Filas de la línea de tiempo: el plan en orden. Una fase sin fechas propias toma las de sus pasos (más tenue), para
 * que se vea cuánto abarca.
 */
export function ganttRows(p: ProjectDetail): GanttRow[] {
  const ordered = planOrder(p.tasks);
  const today = todayIso();
  return ordered.map((task) => {
    const children = ordered.filter((c) => c.parentId === task.id);
    const childStarts = children.map((c) => c.startDate ?? c.dueDate).filter((d): d is string => !!d);
    const childEnds = children.map((c) => c.dueDate ?? c.startDate).filter((d): d is string => !!d);
    const derived = !task.startDate && !task.dueDate && childStarts.length > 0;
    const start = derived ? childStarts.sort()[0]! : task.startDate;
    const end = derived ? childEnds.sort().at(-1)! : task.dueDate;
    const tone: GanttRow['tone'] =
      task.statusCategory === 'terminado'
        ? 'done'
        : task.statusCategory === 'cancelado'
          ? 'cancelled'
          : end && end < today
            ? 'late'
            : task.statusCategory === 'en_curso'
              ? 'active'
              : 'todo';
    return { id: task.id, label: task.title, depth: task.depth, start, end, tone, derived, href: `/tareas/${task.id}` };
  });
}
